/**
 * Canvas overlay shown while a Flow Autobuild pipeline (spec review →
 * authoring → converging → verifying → done) is in flight or has just
 * finished, mounted from `FlowDesignerCanvas` in the same visual family as
 * `StarterPicker`/`GenerationInterstitial`.
 *
 * Deliberately does NOT render during the `authoring` stage — the workflow's
 * own `generating` DB status flips at that point and `GenerationInterstitial`
 * (z-30, vs. this panel's z-10) already owns that overlay via the exact same
 * "author agent is building" signal used elsewhere in the canvas. Rendering
 * both would double-overlay; suppressing here (rather than trying to
 * out-z-index it) keeps a single source of truth for "an author agent is
 * running" instead of two overlapping ones that could drift.
 *
 * @module ui/pages/FlowDesigner/components/AutobuildPipelinePanel
 */
import React from 'react';
import { motion } from 'framer-motion';
import { Link } from '@tanstack/react-router';
import { ArrowSquareOut, CheckCircle, Warning, XCircle, MagicWand } from '@phosphor-icons/react';
import { Button, LoadingOrbit, Text, StatusBadge, Switch, useToast } from '@chatsdet/ui';
import type {
  FlowAutobuildStage,
  FlowAutobuildStatus,
  FlowAutobuildStreamEventData,
} from '../hooks/useFlowAutobuild';
import { useAuth } from '../../../../contexts/AuthContext';
import { useNotificationDeliveryHealth } from '../../../../features/notifications/hooks/useNotificationDeliveryHealth';
import {
  useUserNotificationPreferences,
  useSaveUserNotificationPreference,
} from '../../../../features/notifications/hooks/useUserNotificationPreferences';

const AUTOBUILD_EVENT_KIND = 'flow_autobuild_completed' as const;

type Props = {
  status: FlowAutobuildStatus;
  latestEvent: FlowAutobuildStreamEventData | null;
  onRetry: () => void;
  isRetrying: boolean;
  retryError: string | null;
  onStartOver: () => void;
  onDismiss: () => void;
  onCancel: () => void;
  isCanceling: boolean;
  /** Whether the flow has nodes — a cancel that left none can start over with a new spec. */
  hasNodes: boolean;
};

type StepKey = 'spec_review' | 'authoring' | 'converging' | 'done';

const STEPS: { key: StepKey; label: string }[] = [
  { key: 'spec_review', label: 'Spec Review' },
  { key: 'authoring', label: 'Authoring' },
  { key: 'converging', label: 'Caching' },
  { key: 'done', label: 'Done' },
];

/** Collapses the wire stage into the 4-step stepper (`verifying` folds into `converging`). */
function stepForStage(stage: FlowAutobuildStage): StepKey {
  if (stage === 'verifying') return 'converging';
  if (stage === 'completed') return 'done';
  return stage;
}

function stepStatus(step: StepKey, currentStep: StepKey, isFailed: boolean): 'done' | 'active' | 'pending' | 'failed' {
  const order: StepKey[] = ['spec_review', 'authoring', 'converging', 'done'];
  const currentIdx = order.indexOf(currentStep);
  const stepIdx = order.indexOf(step);
  if (stepIdx < currentIdx) return 'done';
  if (stepIdx === currentIdx) {
    // The pipeline only reaches the `done` step once it is terminal, so that
    // step is never in progress — it resolves to the outcome. Treating it like
    // the others left a finished run spinning on "Done" forever.
    if (step === 'done') return isFailed ? 'failed' : 'done';
    return isFailed ? 'failed' : 'active';
  }
  return 'pending';
}

const TERMINAL_COPY: Record<
  NonNullable<FlowAutobuildStatus['outcome']>,
  { icon: React.ReactNode; headline: string; tone: 'success' | 'warning' | 'error' }
> = {
  converged: {
    icon: <CheckCircle size={22} weight="fill" className="text-green-500" />,
    headline: 'Your flow is ready',
    tone: 'success',
  },
  not_testable: {
    icon: <Warning size={22} weight="fill" className="text-amber-500" />,
    headline: "This spec isn't testable yet",
    tone: 'warning',
  },
  spec_review_failed: {
    icon: <XCircle size={22} weight="fill" className="text-destructive" />,
    headline: 'Spec review failed',
    tone: 'error',
  },
  blocked: {
    icon: <Warning size={22} weight="fill" className="text-amber-500" />,
    headline: 'Convergence got blocked',
    tone: 'warning',
  },
  unverified: {
    icon: <Warning size={22} weight="fill" className="text-amber-500" />,
    headline: 'Flow authored but unverified',
    tone: 'warning',
  },
  error: {
    icon: <XCircle size={22} weight="fill" className="text-destructive" />,
    headline: 'Autobuild hit an error',
    tone: 'error',
  },
  canceled: {
    icon: <Warning size={22} weight="fill" className="text-amber-500" />,
    headline: 'Autobuild canceled',
    tone: 'warning',
  },
};

export const AutobuildPipelinePanel: React.FC<Props> = ({
  status,
  latestEvent,
  onRetry,
  isRetrying,
  retryError,
  onStartOver,
  onDismiss,
  onCancel,
  isCanceling,
  hasNodes,
}) => {
  const currentStep = stepForStage(status.stage);
  const isTerminal = status.stage === 'completed';
  const outcome = status.outcome;
  const isFailedNonTerminalDisplay = isTerminal && outcome !== 'converged';
  const specReviewLink = status.specReview.adhocTestId
    ? { to: '/runs/spec-review/$exploratoryTestId' as const, params: { exploratoryTestId: status.specReview.adhocTestId } }
    : null;

  const retryable =
    outcome === 'blocked' ||
    outcome === 'unverified' ||
    outcome === 'error' ||
    (outcome === 'canceled' && !!status.convergence);
  // A cancel that left no nodes — during review, or during authoring before
  // any node landed — is the dead end "Start over" rebinds with a new spec.
  // Once nodes exist a new spec would be refused as already bound, so Start
  // over disappears and only Retry (re-converge what was authored) remains; a
  // cancel mid-authoring with nothing landed legitimately offers both.
  const canStartOver =
    outcome === 'not_testable' ||
    outcome === 'spec_review_failed' ||
    (outcome === 'canceled' && !hasNodes);

  const { toast } = useToast();
  const { user } = useAuth();
  // Autobuild rides the spec-review flag, notifications ride their own — an
  // org without the latter has no preference routes to call (they 403), so
  // the row is gated on the feature rather than on the fetch failing. The row
  // also only exists in flight; a terminal panel skips both fetches.
  const notifyEnabled = Boolean(user?.features?.notifications) && !isTerminal;
  const healthQuery = useNotificationDeliveryHealth({ enabled: notifyEnabled });
  const health = healthQuery.data?.data;
  const prefsQuery = useUserNotificationPreferences({ enabled: notifyEnabled });
  const savePrefMutation = useSaveUserNotificationPreference({
    onError: (error: Error) => toast.error(error.message),
  });
  const autobuildPref = prefsQuery.data?.data.find((p) => p.eventKind === AUTOBUILD_EVENT_KIND);
  const emailNotifyChecked = Boolean(autobuildPref?.enabled && autobuildPref.emailEnabled);
  const slackNotifyChecked = Boolean(autobuildPref?.enabled && autobuildPref.slackEnabled);
  const showNotifyRow = notifyEnabled && !prefsQuery.isError && prefsQuery.data !== undefined;

  const saveNotifyPref = (nextEmail: boolean, nextSlack: boolean) => {
    savePrefMutation.mutate({
      eventKind: AUTOBUILD_EVENT_KIND,
      pref: { enabled: nextEmail || nextSlack, emailEnabled: nextEmail, slackEnabled: nextSlack },
    });
  };

  // `pt-28` matches the starter-picker overlay in FlowDesignerCanvas — see there
  // for why this region needs a top inset.
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 z-10 flex justify-center overflow-y-auto pt-28 pb-6"
      role="status"
      aria-live="polite"
    >
      <div className="flex flex-col gap-4 my-auto p-6 rounded-xl border border-border bg-background shadow-lg max-w-lg w-full">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-primary/15">
            <MagicWand size={18} weight="duotone" className="text-primary" />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-semibold text-foreground">Autobuild</h3>
          </div>
        </div>

        {/* Stepper */}
        <div className="flex items-start gap-1.5 px-1">
          {STEPS.map((step, idx) => {
            const st = stepStatus(step.key, currentStep, isFailedNonTerminalDisplay);
            return (
              <React.Fragment key={step.key}>
                {idx > 0 && (
                  <div
                    className={`mt-3 h-px flex-1 ${st === 'pending' ? 'bg-border' : 'bg-primary/40'}`}
                  />
                )}
                <div className="flex w-16 flex-shrink-0 flex-col items-center gap-1">
                  <div
                    className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-medium ${
                      st === 'done'
                        ? 'border border-primary bg-primary text-primary-foreground'
                        : // The orbit fills the circle's own 24px footprint, so a
                          // ring around it would read as a double border.
                          st === 'active'
                          ? 'border border-transparent'
                          : st === 'failed'
                            ? 'border border-destructive text-destructive'
                            : 'border border-border text-muted-foreground'
                    }`}
                  >
                    {st === 'done' ? (
                      <CheckCircle size={12} weight="fill" />
                    ) : st === 'active' ? (
                      <LoadingOrbit size="xs" />
                    ) : (
                      idx + 1
                    )}
                  </div>
                  <span
                    className={`text-center text-[10px] font-medium leading-tight ${
                      st === 'pending' ? 'text-muted-foreground' : 'text-foreground'
                    }`}
                  >
                    {step.label}
                  </span>
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {/* In-flight body */}
        {!isTerminal && (
          <div className="flex flex-col gap-3 border-t border-border pt-3">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <StatusBadge
                  status="running"
                  label={
                    status.stage === 'spec_review'
                      ? 'Reviewing spec'
                      : status.stage === 'converging'
                        ? 'Caching'
                        : status.stage === 'verifying'
                          ? 'Verifying'
                          : 'Working'
                  }
                />
                {typeof latestEvent?.cycle === 'number' && (
                  <Text size="xs" className="text-muted-foreground tabular-nums">
                    Cycle {latestEvent.cycle}
                  </Text>
                )}
              </div>
              <Button size="sm" variant="secondary" onPress={onCancel} isDisabled={isCanceling}>
                {isCanceling ? <LoadingOrbit size="sm" /> : null}
                Cancel
              </Button>
            </div>
            {latestEvent?.message && (
              <Text size="sm" className="text-muted-foreground text-pretty">
                {latestEvent.message}
              </Text>
            )}
            {showNotifyRow && (
              <div className="flex items-start justify-between gap-4 rounded-lg border border-border bg-muted/20 px-3 py-2.5">
                <div className="min-w-0">
                  <Text size="sm" weight="medium">
                    Notify me when it&apos;s done
                  </Text>
                  <Text size="xs" className="text-muted-foreground">
                    Applies to every autobuild you start
                  </Text>
                </div>
                <div className="flex shrink-0 items-start gap-4">
                  <div className="flex flex-col items-end gap-1">
                    <Switch
                      label="Email"
                      size="sm"
                      isSelected={emailNotifyChecked}
                      isDisabled={!health?.emailVerified || savePrefMutation.isPending}
                      onChange={(checked) => saveNotifyPref(checked, slackNotifyChecked)}
                    />
                    {!health?.emailVerified && (
                      <Link
                        to="/settings/notifications"
                        target="_blank"
                        className="text-[10px] font-medium text-primary hover:underline"
                      >
                        Verify your email in Settings
                      </Link>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Switch
                      label="Slack"
                      size="sm"
                      isSelected={slackNotifyChecked}
                      isDisabled={!health?.slackLinked || savePrefMutation.isPending}
                      onChange={(checked) => saveNotifyPref(emailNotifyChecked, checked)}
                    />
                    {!health?.slackLinked && (
                      <Link
                        to="/settings/notifications"
                        target="_blank"
                        className="text-[10px] font-medium text-primary hover:underline"
                      >
                        Link your Slack account
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            )}
            {status.stage === 'spec_review' && specReviewLink && (
              <Link
                {...specReviewLink}
                target="_blank"
                className="inline-flex w-fit items-center gap-1 text-xs font-medium text-primary hover:underline"
              >
                Watch the spec review live
                <ArrowSquareOut size={12} />
              </Link>
            )}
          </div>
        )}

        {/* Terminal body */}
        {isTerminal && outcome && (
          <div className="flex flex-col gap-3 border-t border-border pt-3">
            <div className="flex items-start gap-2">
              {TERMINAL_COPY[outcome].icon}
              <div className="min-w-0 flex-1">
                <Text weight="semibold" className="text-sm">
                  {TERMINAL_COPY[outcome].headline}
                </Text>
                {outcome === 'converged' && (
                  <Text size="xs" className="text-muted-foreground">
                    Cache-only replay converged — the flow is authored and ready to run.
                  </Text>
                )}
                {isFailedNonTerminalDisplay && hasNodes && (
                  <Text size="xs" className="text-muted-foreground">
                    The flow was authored — keep it and fix it by hand, or retry caching.
                  </Text>
                )}
                {(outcome === 'not_testable' || outcome === 'spec_review_failed') &&
                  status.specReview.counts && (
                    <Text size="xs" className="text-muted-foreground">
                      {status.specReview.counts.testable} testable ·{' '}
                      {status.specReview.counts.partial} partial ·{' '}
                      {status.specReview.counts.notTestable} not testable
                    </Text>
                  )}
                {status.specReview.flowReadiness?.blockers &&
                  status.specReview.flowReadiness.blockers.length > 0 && (
                    <ul className="mt-1 list-disc pl-4">
                      {status.specReview.flowReadiness.blockers.slice(0, 3).map((b) => (
                        <li key={b} className="text-xs text-muted-foreground text-pretty">
                          {b}
                        </li>
                      ))}
                    </ul>
                  )}
              </div>
            </div>

            {retryError && (
              <Text size="xs" className="text-destructive">
                {retryError}
              </Text>
            )}

            <div className="flex items-center justify-between gap-2">
              {specReviewLink ? (
                <Link
                  {...specReviewLink}
                  target="_blank"
                  className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                >
                  View full report
                  <ArrowSquareOut size={12} />
                </Link>
              ) : (
                <span />
              )}

              <div className="flex items-center gap-2">
                {retryable && (
                  <Button size="sm" variant="secondary" onPress={onRetry} isDisabled={isRetrying}>
                    {isRetrying ? <LoadingOrbit size="sm" /> : null}
                    Retry
                  </Button>
                )}
                {canStartOver && (
                  <Button size="sm" variant="secondary" onPress={onStartOver}>
                    Start over
                  </Button>
                )}
                {isFailedNonTerminalDisplay && hasNodes && (
                  <Button size="sm" variant="primary" onPress={onDismiss}>
                    Keep flow
                  </Button>
                )}
                {outcome === 'converged' && (
                  <Button size="sm" variant="primary" onPress={onDismiss}>
                    View flow
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};
